import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../store';
import { updateUser, logout } from '../../store/authSlice';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../utils/api';
import type { TileConfig } from '../../utils/map-providers';
import {
  Settings,
  X,
  User,
  Link,
  Activity,
  CreditCard,
  ChevronDown,
  Loader2,
  ExternalLink,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { Modal } from '../Modal';
import { useUI } from '../../context/UIContext';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  connections: any[];
  syncingStrava: boolean;
  handleStravaConnect: () => void;
  handleStravaSync: (range?: string) => void;
  setShowDisconnectConfirm: (val: boolean) => void;
  billingInterval: 'month' | 'year';
  setBillingInterval: (val: 'month' | 'year') => void;
  loadingBilling: boolean;
  handleSubscribe: (plan: any, interval: 'month' | 'year') => void;
  handleManageBilling: (plan?: any, interval?: 'month' | 'year') => void;
  settingsActiveTab: 'profile' | 'accounts' | 'activities' | 'billing';
  setSettingsActiveTab: (tab: 'profile' | 'accounts' | 'activities' | 'billing') => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  user,
  connections,
  syncingStrava,
  handleStravaConnect,
  handleStravaSync,
  setShowDisconnectConfirm,
  billingInterval,
  setBillingInterval,
  loadingBilling,
  handleSubscribe,
  handleManageBilling,
  settingsActiveTab,
  setSettingsActiveTab,
}) => {
  const dispatch = useAppDispatch();
  const ui = useUI();
  const syncRangeRef = useRef<HTMLDivElement>(null);
  const [showSyncRangeSelect, setShowSyncRangeSelect] = useState(false);
  const [syncingSubscription, setSyncingSubscription] = useState(false);

  // General User Profile Settings states
  const [profileName, setProfileName] = useState('');
  const [profileUnits, setProfileUnits] = useState<'metric' | 'imperial'>('metric');
  const [profileDateFormat, setProfileDateFormat] = useState('YYYY-MM-DD');
  const [profileTimeFormat, setProfileTimeFormat] = useState<'12h' | '24h'>('24h');
  const [savingProfile, setSavingProfile] = useState(false);

  // Danger Zone / Account Deletion states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [agreePaidCancel, setAgreePaidCancel] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Activity Defaults states
  const [activitySelected, setActivitySelected] = useState<string>('running');
  const [activityCameraProfile, setActivityCameraProfile] = useState('running');
  const [activityResolution, setActivityResolution] = useState('1080p');
  const [activityFps, setActivityFps] = useState(30);
  const [activityDuration, setActivityDuration] = useState(30);
  const [activityAspectRatio, setActivityAspectRatio] = useState('16:9');
  const [activityMapProvider, setActivityMapProvider] = useState('maptiler-satellite');
  const [activityHillshade, setActivityHillshade] = useState(false);
  const [activityExaggeration, setActivityExaggeration] = useState(1.5);
  const [activityIntro, setActivityIntro] = useState(2.0);
  const [activityOutro, setActivityOutro] = useState(3.0);
  const [activityHud, setActivityHud] = useState<Record<string, boolean>>({
    distance: true,
    elevation: true,
    duration: true,
    pace: true,
    heartRate: true,
  });
  const [loadingActivitySettings, setLoadingActivitySettings] = useState(false);
  const [savingActivitySettings, setSavingActivitySettings] = useState(false);
  const [tileConfig, setTileConfig] = useState<TileConfig | null>(null);

  useEffect(() => {
    if (isOpen) {
      apiGet('/api/tiles/config')
        .then((config) => {
          setTileConfig(config);
        })
        .catch((err) => {
          console.error('Failed to load tile config in SettingsModal:', err);
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && user) {
      setProfileName(user.name);
      setProfileUnits(user.units || 'metric');
      setProfileDateFormat(user.dateFormat || 'YYYY-MM-DD');
      setProfileTimeFormat(user.timeFormat || '24h');
    }
  }, [isOpen, user]);

  const fetchActivitySettings = async (type: string) => {
    setLoadingActivitySettings(true);
    try {
      const data = await apiGet(`/api/tracks/settings/${type}`);
      if (data) {
        setActivityCameraProfile(data.cameraProfileName || 'running');
        setActivityResolution(data.resolution || '1080p');
        setActivityFps(data.fps || 30);
        setActivityDuration(data.customDuration || 30);
        setActivityAspectRatio(data.aspectRatio || '16:9');
        setActivityMapProvider(data.mapProvider || 'maptiler-satellite');
        setActivityHillshade(!!data.hillshade);
        setActivityExaggeration(data.terrainExaggeration ?? 1.5);
        setActivityIntro(data.introDuration ?? 2.0);
        setActivityOutro(data.outroDuration ?? 3.0);
        setActivityHud(
          data.hudSettings || {
            distance: true,
            elevation: true,
            duration: true,
            pace: true,
            heartRate: true,
          }
        );
      }
    } catch (err) {
      console.error('Failed to load activity defaults:', err);
    } finally {
      setLoadingActivitySettings(false);
    }
  };

  useEffect(() => {
    if (isOpen && settingsActiveTab === 'activities') {
      fetchActivitySettings(activitySelected);
    }
  }, [isOpen, settingsActiveTab, activitySelected]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (syncRangeRef.current && !syncRangeRef.current.contains(e.target as Node)) {
        setShowSyncRangeSelect(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleSyncSubscription = async () => {
    setSyncingSubscription(true);
    try {
      await apiPost('/api/stripe/sync', {});
      const profile = await apiGet('/api/auth/profile');
      if (profile?.user) {
        dispatch(updateUser(profile.user));
        ui.toast('Subscription status successfully synchronized!', 'success');
      }
    } catch (err: any) {
      console.error('Failed to sync subscription:', err);
      ui.alert(err.message || 'Failed to sync subscription status.', 'Sync Error');
    } finally {
      setSyncingSubscription(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const data = await apiPatch('/api/auth/settings', {
        name: profileName,
        units: profileUnits,
        dateFormat: profileDateFormat,
        timeFormat: profileTimeFormat,
      });
      if (data?.user) {
        dispatch(updateUser(data.user));
        ui.toast('Profile settings saved successfully', 'success');
      }
    } catch (err: any) {
      ui.toast(err.message || 'Failed to save profile settings', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const hasActivePaidPlan = user?.plan && user.plan !== 'free';

  const handleDeleteAccount = async () => {
    if (deleteConfirmEmail.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase()) {
      ui.toast('Email confirmation does not match your account email', 'error');
      return;
    }
    if (hasActivePaidPlan && !agreePaidCancel) {
      ui.toast('You must acknowledge that active paid subscriptions will be cancelled without refund', 'error');
      return;
    }

    setDeletingAccount(true);
    try {
      await apiDelete('/api/auth/account');
      ui.toast('Your account has been permanently deleted.', 'info');
      dispatch(logout());
      onClose();
      window.location.href = '/login';
    } catch (err: any) {
      console.error('Account deletion failed:', err);
      ui.toast(err.message || 'Failed to delete account', 'error');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleSaveActivitySettings = async () => {
    setSavingActivitySettings(true);
    try {
      await apiPost(`/api/tracks/settings/${activitySelected}`, {
        cameraProfileName: activityCameraProfile,
        resolution: activityResolution,
        fps: activityFps,
        customDuration: activityDuration,
        aspectRatio: activityAspectRatio,
        mapProvider: activityMapProvider,
        hillshade: activityHillshade,
        terrainExaggeration: activityExaggeration,
        introDuration: activityIntro,
        outroDuration: activityOutro,
        hudSettings: activityHud,
      });
      ui.toast(`Default settings for ${activitySelected} saved`, 'success');
    } catch (err: any) {
      ui.toast(err.message || 'Failed to save activity defaults', 'error');
    } finally {
      setSavingActivitySettings(false);
    }
  };

  const isStravaLinked = connections.find((c) => c.provider === 'strava')?.linked;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="850px" height="80vh" maxHeight="680px">
      {/* Modal Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <span
          style={{
            fontWeight: 800,
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            letterSpacing: '-0.02em',
            color: '#fff',
          }}
        >
          <Settings size={18} style={{ color: 'var(--accent-primary)' }} />
          <span>Control Panel & Settings</span>
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: 'none',
            borderRadius: '50%',
            color: '#fff',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Modal Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Tabs Sidebar */}
        <div
          style={{
            width: '220px',
            borderRight: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(0,0,0,0.15)',
            padding: '16px 8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <button
            onClick={() => setSettingsActiveTab('profile')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: settingsActiveTab === 'profile' ? 'rgba(255,255,255,0.06)' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: settingsActiveTab === 'profile' ? '#fff' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
          >
            <User size={16} />
            <span>Profile & General</span>
          </button>

          <button
            onClick={() => setSettingsActiveTab('accounts')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: settingsActiveTab === 'accounts' ? 'rgba(255,255,255,0.06)' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: settingsActiveTab === 'accounts' ? '#fff' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
          >
            <Link size={16} />
            <span>Linked Accounts</span>
          </button>

          <button
            onClick={() => setSettingsActiveTab('activities')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: settingsActiveTab === 'activities' ? 'rgba(255,255,255,0.06)' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: settingsActiveTab === 'activities' ? '#fff' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
          >
            <Activity size={16} />
            <span>Activity Profiles</span>
          </button>

          <button
            onClick={() => setSettingsActiveTab('billing')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: settingsActiveTab === 'billing' ? 'rgba(255,255,255,0.06)' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: settingsActiveTab === 'billing' ? '#fff' : 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
          >
            <CreditCard size={16} />
            <span>Billing & Plan</span>
          </button>
        </div>

        {/* Tab Content Panel */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* 1. Profile Tab */}
          {settingsActiveTab === 'profile' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                Profile & Preferences
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  className="text-input"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    color: '#fff',
                    width: '100%',
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Unit System
                </label>
                <select
                  className="text-input"
                  value={profileUnits}
                  onChange={(e) => setProfileUnits(e.target.value as 'metric' | 'imperial')}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    color: '#fff',
                    width: '100%',
                  }}
                >
                  <option value="metric">Metric (meters, kilometers, km/h)</option>
                  <option value="imperial">Imperial (feet, miles, mph)</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Date Format
                </label>
                <select
                  className="text-input"
                  value={profileDateFormat}
                  onChange={(e) => setProfileDateFormat(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    color: '#fff',
                    width: '100%',
                  }}
                >
                  <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-07-01)</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 01/07/2026)</option>
                  <option value="DD.MM.YYYY">DD.MM.YYYY (e.g. 01.07.2026)</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 07/01/2026)</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Time Format
                </label>
                <select
                  className="text-input"
                  value={profileTimeFormat}
                  onChange={(e) => setProfileTimeFormat(e.target.value as '12h' | '24h')}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    color: '#fff',
                    width: '100%',
                  }}
                >
                  <option value="24h">24-hour clock (e.g. 17:00)</option>
                  <option value="12h">12-hour clock (e.g. 5:00 PM)</option>
                </select>
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px', marginTop: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                  Cookie Preferences
                </label>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Manage your Google Analytics cookie consent preferences.
                  </span>
                  <button
                    onClick={() => {
                      localStorage.removeItem('cookie-consent');
                      window.dispatchEvent(new Event('cookie-consent-reset'));
                      ui.toast('Cookie preferences reset. Consent banner displayed.', 'success');
                    }}
                    className="btn"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: '#fff',
                      fontSize: '11px',
                      padding: '6px 14px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    Reset Settings
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="btn btn-primary"
                  style={{ padding: '10px 24px', borderRadius: '20px' }}
                >
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>

              {/* Danger Zone */}
              <div style={{ borderTop: '1px solid rgba(239, 68, 68, 0.2)', paddingTop: '20px', marginTop: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <AlertTriangle size={15} />
                  <span>Danger Zone</span>
                </label>
                <div style={{ background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#fff', display: 'block' }}>
                      Delete Account & All Data
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Permanently delete your account, saved tracks, rendered MP4 videos, and connected integrations. This action is irreversible.
                    </span>
                  </div>

                  {!showDeleteConfirm ? (
                    <div>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(true);
                          setDeleteConfirmEmail('');
                          setAgreePaidCancel(false);
                        }}
                        className="btn"
                        style={{
                          background: 'rgba(239, 68, 68, 0.12)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          color: '#ef4444',
                          fontSize: '12px',
                          padding: '8px 16px',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <Trash2 size={14} />
                        <span>Delete Account</span>
                      </button>
                    </div>
                  ) : (
                    <div style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <span style={{ fontSize: '13px', color: '#f87171', fontWeight: 600 }}>
                        Confirm Permanent Account Deletion
                      </span>
                      <span style={{ fontSize: '12px', color: '#d1d5db' }}>
                        Please type your account email <strong style={{ color: '#fff' }}>{user?.email}</strong> below to confirm deletion.
                      </span>
                      <input
                        type="email"
                        placeholder={user?.email || 'Enter your email'}
                        value={deleteConfirmEmail}
                        onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          color: '#fff',
                          fontSize: '13px',
                          width: '100%',
                        }}
                      />

                      {hasActivePaidPlan && (
                        <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#fbbf24', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AlertTriangle size={14} />
                            <span>Active Subscription Notice</span>
                          </span>
                          <span style={{ fontSize: '11px', color: '#fef3c7', lineHeight: '1.4' }}>
                            You currently have an active <strong>{String(user?.plan).toUpperCase()}</strong> subscription. Deleting your account will immediately cancel your subscription in Stripe without automated pro-rated refunds.
                          </span>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginTop: '4px' }}>
                            <input
                              type="checkbox"
                              checked={agreePaidCancel}
                              onChange={(e) => setAgreePaidCancel(e.target.checked)}
                              style={{ marginTop: '2px' }}
                            />
                            <span style={{ fontSize: '11px', color: '#fff', fontWeight: 500 }}>
                              I understand that my active subscription will be immediately cancelled without refund.
                            </span>
                          </label>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={deletingAccount}
                          className="btn"
                          style={{
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#fff',
                            padding: '6px 14px',
                            fontSize: '12px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={
                            deletingAccount ||
                            deleteConfirmEmail.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase() ||
                            (hasActivePaidPlan && !agreePaidCancel)
                          }
                          className="btn"
                          style={{
                            background: 'rgba(239, 68, 68, 0.9)',
                            border: 'none',
                            color: '#fff',
                            padding: '6px 16px',
                            fontSize: '12px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: 600,
                            opacity:
                              deleteConfirmEmail.trim().toLowerCase() === (user?.email || '').trim().toLowerCase() &&
                              (!hasActivePaidPlan || agreePaidCancel)
                                ? 1
                                : 0.5,
                            cursor:
                              deleteConfirmEmail.trim().toLowerCase() === (user?.email || '').trim().toLowerCase() &&
                              (!hasActivePaidPlan || agreePaidCancel)
                                ? 'pointer'
                                : 'not-allowed',
                          }}
                        >
                          {deletingAccount ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />}
                          <span>{deletingAccount ? 'Deleting...' : 'Permanently Delete Account'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 2. Linked Accounts Tab */}
          {settingsActiveTab === 'accounts' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                  Integrations & Quotas
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                  Manage external accounts and monitor your monthly rendering limits.
                </p>
              </div>

              {/* Strava link row */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'rgba(252, 82, 0, 0.1)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fc5200',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.6h3.065L11.214 0 5.343 11.745h3.063" />
                    </svg>
                  </div>
                  <div>
                    <strong style={{ display: 'block', fontSize: '14px', color: '#fff' }}>Strava</strong>
                    {isStravaLinked ? (
                      <span
                        style={{
                          fontSize: '11px',
                          color: '#10b981',
                          background: 'rgba(16,185,129,0.08)',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          marginTop: '2px',
                        }}
                      >
                        Linked
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: '11px',
                          color: 'var(--text-muted)',
                          background: 'rgba(255,255,255,0.04)',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          marginTop: '2px',
                        }}
                      >
                        Not connected
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  {isStravaLinked ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div ref={syncRangeRef} style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          onClick={() => setShowSyncRangeSelect(!showSyncRangeSelect)}
                          disabled={syncingStrava}
                          className="btn btn-secondary"
                          style={{
                            padding: '8px 14px',
                            borderRadius: '18px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(252, 82, 0, 0.1)',
                            border: '1px solid rgba(252, 82, 0, 0.2)',
                            color: '#fc5200',
                          }}
                        >
                          {syncingStrava ? (
                            <div
                              className="spinner"
                              style={{
                                width: '12px',
                                height: '12px',
                                borderWidth: '1.5px',
                                borderColor: '#fc5200 transparent transparent transparent',
                              }}
                            />
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.6h3.065L11.214 0 5.343 11.745h3.063" />
                            </svg>
                          )}
                          <span>{syncingStrava ? 'Syncing...' : 'Sync Activities'}</span>
                          <ChevronDown size={12} style={{ opacity: 0.7 }} />
                        </button>

                        {showSyncRangeSelect && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 8px)',
                              right: 0,
                              width: '180px',
                              background: 'rgba(15, 23, 42, 0.98)',
                              backdropFilter: 'blur(12px)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '8px',
                              padding: '4px',
                              zIndex: 100,
                              boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: '10px',
                                color: 'var(--text-secondary)',
                                padding: '6px 8px 4px 8px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              Sync Time Range
                            </div>
                            {[
                              { label: 'Last 7 Days', value: '7days' },
                              { label: 'Last 30 Days', value: '30days' },
                              { label: 'Last 90 Days', value: '90days' },
                              { label: 'All Activities', value: 'all' },
                            ].map((opt) => (
                              <button
                                key={opt.value}
                                onClick={() => {
                                  handleStravaSync(opt.value);
                                  setShowSyncRangeSelect(false);
                                }}
                                style={{
                                  padding: '8px 10px',
                                  background: 'transparent',
                                  border: 'none',
                                  borderRadius: '6px',
                                  color: 'var(--text-primary)',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => setShowDisconnectConfirm(true)}
                        className="btn"
                        style={{
                          padding: '8px 14px',
                          borderRadius: '18px',
                          fontSize: '12px',
                          background: 'rgba(239, 68, 68, 0.08)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleStravaConnect}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px', borderRadius: '18px', fontSize: '12px' }}
                    >
                      Connect Strava
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 3. Activity Defaults Tab */}
          {settingsActiveTab === 'activities' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                  Activity Type Default Profiles
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                  Customize default rendering options loaded when previewing specific sport types.
                </p>
              </div>

              {/* Activity Selector Buttons */}
              <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px' }}>
                {['running', 'cycling', 'hiking', 'climbing'].map((sport) => (
                  <button
                    key={sport}
                    onClick={() => setActivitySelected(sport)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      cursor: 'pointer',
                      background: activitySelected === sport ? 'var(--accent-gradient)' : 'rgba(255,255,255,0.03)',
                      border: '1px solid ' + (activitySelected === sport ? 'transparent' : 'rgba(255,255,255,0.06)'),
                      color: '#fff',
                    }}
                  >
                    {sport}
                  </button>
                ))}
              </div>

              {loadingActivitySettings ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <Loader2 className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Defaults Form */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Camera Animation Profile
                      </label>
                      <select
                        className="text-input"
                        value={activityCameraProfile}
                        onChange={(e) => setActivityCameraProfile(e.target.value)}
                        style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' }}
                      >
                        <option value="running">Running (Smooth Chase, High-Angle)</option>
                        <option value="cycling">Cycling (Cinematic Fly-by, Low-Angle)</option>
                        <option value="hiking">Hiking (Orbit Center, Slow Panorama)</option>
                        <option value="climbing">Climbing (Close-up, High Elevation Exaggeration)</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Map Layers Provider
                      </label>
                      <select
                        className="text-input"
                        value={activityMapProvider}
                        onChange={(e) => setActivityMapProvider(e.target.value)}
                        style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' }}
                      >
                        {!tileConfig ? (
                          <option value="">Loading providers...</option>
                        ) : (
                          tileConfig.providers.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Default Resolution
                      </label>
                      <select
                        className="text-input"
                        value={activityResolution}
                        onChange={(e) => setActivityResolution(e.target.value)}
                        style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' }}
                      >
                        <option value="720p">720p (HD, Fast Render)</option>
                        <option value="1080p">1080p (Full HD, Balanced)</option>
                        <option value="4K">4K (Ultra HD, Premium Only)</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Aspect Ratio
                      </label>
                      <select
                        className="text-input"
                        value={activityAspectRatio}
                        onChange={(e) => setActivityAspectRatio(e.target.value)}
                        style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' }}
                      >
                        <option value="16:9">16:9 Widescreen (YouTube, TV)</option>
                        <option value="9:16">9:16 Vertical (TikTok, Reels, Shorts)</option>
                        <option value="1:1">1:1 Square (Instagram Post)</option>
                        <option value="4:3">4:3 Classic</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Target Framerate (FPS)
                      </label>
                      <select
                        className="text-input"
                        value={activityFps}
                        onChange={(e) => setActivityFps(Number(e.target.value))}
                        style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' }}
                      >
                        <option value={30}>30 FPS (Standard Cinematic)</option>
                        <option value={60}>60 FPS (Ultra Smooth, Premium Only)</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Custom Video Duration (seconds)
                      </label>
                      <input
                        type="number"
                        min={10}
                        max={180}
                        className="text-input"
                        value={activityDuration}
                        onChange={(e) => setActivityDuration(Number(e.target.value))}
                        style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '13px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Terrain Exaggeration ({activityExaggeration}x)
                      </label>
                      <input
                        type="range"
                        min={1.0}
                        max={3.0}
                        step={0.1}
                        value={activityExaggeration}
                        onChange={(e) => setActivityExaggeration(Number(e.target.value))}
                        style={{ width: '100%', cursor: 'pointer' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Intro Duration ({activityIntro}s)
                      </label>
                      <input
                        type="range"
                        min={0.0}
                        max={5.0}
                        step={0.5}
                        value={activityIntro}
                        onChange={(e) => setActivityIntro(Number(e.target.value))}
                        style={{ width: '100%', cursor: 'pointer' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        Outro Duration ({activityOutro}s)
                      </label>
                      <input
                        type="range"
                        min={0.0}
                        max={8.0}
                        step={0.5}
                        value={activityOutro}
                        onChange={(e) => setActivityOutro(Number(e.target.value))}
                        style={{ width: '100%', cursor: 'pointer' }}
                      />
                    </div>
                  </div>

                  {/* Toggle items */}
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={activityHillshade}
                        onChange={(e) => setActivityHillshade(e.target.checked)}
                        style={{ accentColor: 'var(--accent-primary)' }}
                      />
                      <span>Enable Terrain Hillshading</span>
                    </label>
                  </div>

                  {/* HUD options */}
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '16px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                      Default HUD Telemetry Widgets
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
                      {Object.keys(activityHud).map((widget) => (
                        <label key={widget} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', textTransform: 'capitalize' }}>
                          <input
                            type="checkbox"
                            checked={activityHud[widget]}
                            onChange={(e) =>
                              setActivityHud((prev) => ({ ...prev, [widget]: e.target.checked }))
                            }
                            style={{ accentColor: 'var(--accent-primary)' }}
                          />
                          <span>{widget === 'heartRate' ? 'Heart Rate' : widget}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button
                      onClick={handleSaveActivitySettings}
                      disabled={savingActivitySettings}
                      className="btn btn-primary"
                      style={{ padding: '10px 24px', borderRadius: '20px' }}
                    >
                      {savingActivitySettings ? 'Saving defaults...' : 'Save Profile Defaults'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 4. Billing Tab */}
          {settingsActiveTab === 'billing' && (() => {
            const PLAN_TIERS = ['free', 'starter', 'pro', 'expert', 'ultra'] as const;
            const currentPlan = (user?.plan || 'free') as string;
            const currentIdx = PLAN_TIERS.indexOf(currentPlan as any);

            const plans = [
              {
                id: 'free', name: 'Free', emoji: '🆓',
                monthlyPrice: 0, annualPrice: 0, annualTotal: 0,
                tagline: 'Try it out',
                features: [
                  { label: 'Renders', value: '5× 720p · 1× 1080p' },
                  { label: '4K Renders', value: '—' },
                  { label: 'Track Length', value: '20 km' },
                  { label: 'Video Storage', value: '3 days' },
                  { label: 'Satellite Maps', value: '—' },
                  { label: 'Queue Priority', value: 'Standard' },
                ],
              },
              {
                id: 'starter', name: 'Starter', emoji: '⚡',
                monthlyPrice: 0.99, annualPrice: 0.83, annualTotal: 9.90,
                tagline: 'For casual creators',
                features: [
                  { label: 'Renders', value: '15× 720p · 5× 1080p' },
                  { label: '4K Renders', value: '1 / month' },
                  { label: 'Track Length', value: '30 km' },
                  { label: 'Video Storage', value: '5 days' },
                  { label: 'Satellite Maps', value: '—' },
                  { label: 'Queue Priority', value: 'Enhanced' },
                ],
              },
              {
                id: 'pro', name: 'Pro', emoji: '🔥',
                monthlyPrice: 2.49, annualPrice: 2.08, annualTotal: 24.90,
                tagline: 'Most popular',
                popular: true,
                features: [
                  { label: 'Renders', value: '50× 720p · 10× 1080p' },
                  { label: '4K Renders', value: '5 / month' },
                  { label: 'Track Length', value: '100 km' },
                  { label: 'Video Storage', value: '14 days' },
                  { label: 'Satellite Maps', value: '2× MapTiler / mo' },
                  { label: 'Queue Priority', value: 'High' },
                ],
              },
              {
                id: 'expert', name: 'Expert', emoji: '💎',
                monthlyPrice: 4.99, annualPrice: 4.16, annualTotal: 49.90,
                tagline: 'For power users',
                features: [
                  { label: 'Renders', value: '100× 720p · 30× 1080p' },
                  { label: '4K Renders', value: '15 / month' },
                  { label: 'Track Length', value: '250 km' },
                  { label: 'Video Storage', value: '21 days' },
                  { label: 'Satellite Maps', value: '10× MapTiler / mo' },
                  { label: 'Queue Priority', value: 'Very High' },
                ],
              },
              {
                id: 'ultra', name: 'Ultra', emoji: '🚀',
                monthlyPrice: 9.99, annualPrice: 8.33, annualTotal: 99.90,
                tagline: 'No limits',
                ultra: true,
                features: [
                  { label: 'Renders', value: '∞ 720p · ∞ 1080p' },
                  { label: '4K Renders', value: '∞' },
                  { label: 'Track Length', value: '500 km' },
                  { label: 'Video Storage', value: '30 days' },
                  { label: 'Satellite Maps', value: '∞ MapTiler' },
                  { label: 'Queue Priority', value: 'Maximum' },
                ],
              },
            ];

            const getAction = (planId: string) => {
              const planIdx = PLAN_TIERS.indexOf(planId as any);
              const isCurrent = planId === currentPlan;
              const isUpgrade = planIdx > currentIdx;

              if (isCurrent) return { text: 'Current Plan', disabled: true, type: 'current' as const };
              if (currentPlan === 'free') return { text: 'Get Started', disabled: false, type: 'subscribe' as const };
              if (planId === 'free') return { text: 'Downgrade', disabled: false, type: 'downgrade' as const };
              return {
                text: isUpgrade ? 'Upgrade' : 'Downgrade',
                disabled: false,
                type: isUpgrade ? 'upgrade' as const : 'downgrade' as const,
              };
            };

            const handleAction = async (planId: string, actionType: string) => {
              if (actionType === 'subscribe') {
                handleSubscribe(planId as any, billingInterval);
              } else if (actionType === 'downgrade') {
                const ok = await ui.confirm(
                  `Are you sure you want to downgrade? You'll lose access to higher quotas, 4K resolution options, and premium satellite layers.`,
                  'Downgrade Plan?',
                  'warning'
                );
                if (!ok) return;
                handleManageBilling(planId, billingInterval);
              } else if (actionType === 'upgrade') {
                handleManageBilling(planId, billingInterval);
              }
            };

            const isAnnual = billingInterval === 'year';

            /* Shared card renderer */
            const renderCard = (plan: typeof plans[0], opts?: { wide?: boolean }) => {
              const action = getAction(plan.id);
              const isPopular = (plan as any).popular;
              const isUltra = (plan as any).ultra;
              const isCurrent = plan.id === currentPlan;
              const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;

              const accentColor = isPopular ? '#ff385c' : isUltra ? '#a855f7' : '#10b981';
              const cardBg = isPopular
                ? 'linear-gradient(180deg, rgba(255,56,92,0.06) 0%, rgba(255,56,92,0.02) 100%)'
                : isUltra
                ? 'linear-gradient(180deg, rgba(168,85,247,0.08) 0%, rgba(236,72,153,0.03) 100%)'
                : 'rgba(255,255,255,0.02)';
              const cardBorder = isCurrent
                ? `1.5px solid ${accentColor}`
                : isPopular
                ? '1px solid rgba(255,56,92,0.35)'
                : isUltra
                ? '1px solid rgba(168,85,247,0.35)'
                : '1px solid rgba(255,255,255,0.07)';
              const cardShadow = isPopular
                ? '0 4px 24px rgba(255,56,92,0.12)'
                : isUltra
                ? '0 4px 24px rgba(168,85,247,0.12)'
                : 'none';

              return (
                <div
                  key={plan.id}
                  style={{
                    position: 'relative',
                    background: cardBg,
                    border: cardBorder,
                    borderRadius: '16px',
                    padding: opts?.wide ? '20px 24px' : '18px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    boxShadow: cardShadow,
                    transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.borderColor = accentColor;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.borderColor = isPopular
                        ? 'rgba(255,56,92,0.35)'
                        : isUltra
                        ? 'rgba(168,85,247,0.35)'
                        : 'rgba(255,255,255,0.07)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {/* Badge */}
                  {(isPopular || isUltra || isCurrent) && (
                    <div style={{
                      position: 'absolute', top: 0, right: 0,
                      background: isCurrent
                        ? 'linear-gradient(135deg, #10b981, #059669)'
                        : isPopular
                        ? 'var(--accent-gradient)'
                        : 'linear-gradient(135deg, #a855f7, #ec4899)',
                      color: '#fff', fontSize: '9px', fontWeight: 700,
                      padding: '3px 10px 3px 12px',
                      borderBottomLeftRadius: '10px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}>
                      {isCurrent ? '✓ Current' : isPopular ? '★ Popular' : '⚡ Ultimate'}
                    </div>
                  )}

                  {/* Plan Header */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '15px' }}>{plan.emoji}</span>
                      <span style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{plan.name}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{plan.tagline}</span>
                  </div>

                  {/* Price */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                      <span style={{ fontSize: '28px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                        €{price === 0 ? '0' : price.toFixed(2).replace('.', ',')}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '2px' }}>/ mo</span>
                    </div>
                    {plan.id !== 'free' && (
                      <div style={{ fontSize: '11px', marginTop: '4px', lineHeight: 1.3 }}>
                        {isAnnual ? (
                          <span>
                            <span style={{ color: '#10b981', fontWeight: 600 }}>€{plan.annualTotal.toFixed(2).replace('.', ',')} / year</span>
                            <span style={{ color: 'var(--text-muted)', marginLeft: '6px', textDecoration: 'line-through' }}>
                              €{(plan.monthlyPrice * 12).toFixed(2).replace('.', ',')}
                            </span>
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>
                            or <button
                              onClick={() => setBillingInterval('year')}
                              style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', fontWeight: 600, fontSize: '11px', padding: 0, fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                            >save ~17% with annual</button>
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                    {plan.features.map((f, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11.5px', lineHeight: 1.3 }}>
                        <span style={{ color: 'var(--text-muted)' }}>{f.label}</span>
                        <span style={{ color: f.value === '—' ? 'var(--text-muted)' : (f.value.startsWith('∞') ? accentColor : '#fff'), fontWeight: f.value === '—' ? 400 : 600, textAlign: 'right' }}>
                          {f.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleAction(plan.id, action.type)}
                    disabled={action.disabled || loadingBilling}
                    className="btn"
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      background: action.disabled
                        ? 'rgba(255,255,255,0.03)'
                        : isPopular
                        ? 'var(--accent-gradient)'
                        : isUltra
                        ? 'linear-gradient(135deg, #a855f7, #ec4899)'
                        : action.type === 'downgrade'
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(255,255,255,0.06)',
                      color: action.disabled
                        ? 'var(--text-muted)'
                        : (isPopular || isUltra || action.type === 'subscribe' || action.type === 'upgrade')
                        ? '#fff'
                        : 'var(--text-secondary)',
                      border: action.disabled
                        ? '1px solid rgba(255,255,255,0.04)'
                        : (isPopular || isUltra)
                        ? 'none'
                        : '1px solid rgba(255,255,255,0.1)',
                      cursor: action.disabled ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: (!action.disabled && isPopular)
                        ? '0 2px 12px rgba(255,56,92,0.3)'
                        : (!action.disabled && isUltra)
                        ? '0 2px 12px rgba(168,85,247,0.3)'
                        : 'none',
                    }}
                  >
                    {loadingBilling ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      action.text
                    )}
                  </button>
                </div>
              );
            };

            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Header */}
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
                  Choose Your Plan
                </h3>
                <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', margin: 0 }}>
                  Unlock more renders, longer tracks, and premium satellite maps
                </p>
              </div>

              {/* Billing Toggle */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  display: 'flex',
                  background: 'rgba(0,0,0,0.35)',
                  borderRadius: '28px',
                  padding: '3px',
                  border: '1px solid rgba(255,255,255,0.06)',
                  position: 'relative',
                }}>
                  <button
                    onClick={() => setBillingInterval('month')}
                    style={{
                      padding: '7px 18px',
                      borderRadius: '24px',
                      fontSize: '12px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 0.25s ease',
                      background: !isAnnual ? 'rgba(255,255,255,0.1)' : 'transparent',
                      color: !isAnnual ? '#fff' : 'var(--text-muted)',
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingInterval('year')}
                    style={{
                      padding: '7px 18px',
                      borderRadius: '24px',
                      fontSize: '12px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 0.25s ease',
                      background: isAnnual ? 'rgba(16,185,129,0.15)' : 'transparent',
                      color: isAnnual ? '#10b981' : 'var(--text-muted)',
                    }}
                  >
                    Annual
                    <span style={{
                      marginLeft: '6px',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      fontSize: '9px',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '8px',
                      letterSpacing: '0.3px',
                    }}>
                      −17%
                    </span>
                  </button>
                </div>
              </div>

              {/* Stripe Portal Link (paid users) */}
              {user?.plan !== 'free' && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'rgba(16,185,129,0.06)',
                  border: '1px solid rgba(16,185,129,0.18)',
                  borderRadius: '12px',
                  padding: '10px 14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: '#10b981',
                      boxShadow: '0 0 6px rgba(16,185,129,0.5)',
                    }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Active <strong style={{ color: '#fff', textTransform: 'capitalize' }}>{user?.plan}</strong> plan
                    </span>
                  </div>
                  <button
                    onClick={() => handleManageBilling(undefined, undefined)}
                    disabled={loadingBilling}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px', padding: '5px 10px',
                      color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  >
                    Manage Billing <ExternalLink size={10} />
                  </button>
                </div>
              )}

              {/* Plan Cards — Row 1: Free, Starter, Pro */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {plans.slice(0, 3).map(p => renderCard(p))}
              </div>

              {/* Plan Cards — Row 2: Expert, Ultra */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {plans.slice(3).map(p => renderCard(p, { wide: true }))}
              </div>

              {/* Footer */}
              <p style={{ fontSize: '10.5px', color: 'var(--text-muted)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
                All prices include VAT. Cancel anytime via the Stripe billing portal.
              </p>

              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <button
                  onClick={handleSyncSubscription}
                  disabled={syncingSubscription}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--accent-primary)',
                    fontSize: '11px',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    opacity: syncingSubscription ? 0.6 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  {syncingSubscription ? 'Syncing...' : 'Already subscribed? Refresh status'}
                </button>
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    </Modal>
  );
};
